import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {
  borderRadiusStandard,
  focusOutlineColor,
  focusOutlineStyle,
  focusOutlineWidth,
  focusOutlineOffset,
  colorWhite,
  colorDarkest,
  validBreakpointValues,
  SpacingType,
  BreakpointType
} from '@nib-components/theme';
import {Box, Columns, Column, Stack, spacingPropTypeMap, mapFlexAlignment, breakpoint, p} from '@nib/layout';
import Heading from '@nib-components/heading';
import Divider from '@nib-components/divider';
import {IconProps} from '@nib/icons';
import Tag from '@nib/tag';

export const validAlignValues = ['left', 'right', 'center'] as const;
export const validTitleComponentValues = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'label', 'span', 'header'] as const;
export const validTitleSizeValues = [1, 2, 3, 4, 5, 6] as const;
export const validBackgroundValues = ['light', 'white', 'sageGreen', 'warmWhite', 'sunsetPink'] as const;

export type alignValues = (typeof validAlignValues)[number];
export type titleSizeValues = (typeof validTitleSizeValues)[number];
export type titleComponentValues = (typeof validTitleComponentValues)[number];
export type backgroundValues = (typeof validBackgroundValues)[number];

type imageTypes = string | React.ReactElement;

type Nullable<T> = T | null;

type PartialRecord<K extends keyof never, T> = {
  [P in K]?: T;
};

type ResponsiveProp<T> = PartialRecord<BreakpointType, T>;
type ResponsiveOrStaticProp<T> = T | ResponsiveProp<T>;

export interface BaseCardProps {
  // 'unknown' is required due to our use of GatsbyJS's Link. In the future, including Gatsby types would be better
  component?: unknown;
  align?: alignValues;
  padding?: ResponsiveOrStaticProp<Nullable<SpacingType>>;
  href?: string;
  to?: string;
  height?: string;
  background?: backgroundValues;
  [key: string]: unknown; // other props
}

export interface CardProps extends BaseCardProps {
  icon?: React.FC<IconProps>;
  image?: imageTypes;
  imageAlt?: string;
  imageLazyLoading?: boolean;
  tag?: typeof Tag;
  horizontalLayout?: boolean;
  reverse?: boolean;
  collapseBelow?: BreakpointType;
  children: React.ReactNode;
}

export interface CardContentProps {
  title: string;
  titleSize?: titleSizeValues;
  titleComponent?: titleComponentValues;
  padding?: ResponsiveOrStaticProp<Nullable<SpacingType>>;
  align?: alignValues;
  children?: React.ReactNode;
}

export interface CardFooterProps {
  padding?: ResponsiveOrStaticProp<Nullable<SpacingType>>;
  align?: alignValues;
  children?: React.ReactNode;
}

export interface FlashCardProps extends BaseCardProps {
  icon: React.FC<IconProps>;
  children: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const CardWrapper = styled(({component: Component, horizontalLayout, collapseBelow, imageAlt, ...rest}) => <Component {...rest} />)`
  display: flex;
  flex-direction: column; // the default

  ${({horizontalLayout, collapseBelow}) =>
    horizontalLayout &&
    collapseBelow &&
    css`
      ${breakpoint(collapseBelow)`
        flex-direction: row;
      `};
    `}

  color: ${colorDarkest};
  background-color: ${colorWhite};
  text-decoration: none;
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.13);
  border-radius: ${props => (props.theme.id !== 'nib' ? borderRadiusStandard || '4px' : '8px')};
  width: 100%;
  overflow: hidden;

  ${({height}) =>
    height &&
    css`
      min-height: ${height};
    `}

  ${({component}) =>
    component !== 'div' &&
    css`
      cursor: pointer;
      transition: transform 0.2s ease;

      &:focus {
        outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
        outline-offset: ${focusOutlineOffset};
      }

      &:hover {
        transform: translate(0, -3px);
      }

      &:active {
        transform: translate(0, -2px);
      }
    `}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CardHeader = styled(({align, height, ...rest}) => <Box {...rest} />)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: ${props => mapFlexAlignment(props.align)};
  width: 100%;

  img {
    max-width: 100%;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CardContentWrapper = styled(({padding, align, grow, ...rest}) => <div {...rest} />)`
  display: flex;
  flex-direction: column;
  align-items: ${props => mapFlexAlignment(props.align)};
  ${({grow}) => grow && 'flex-grow: 1'};
  text-align: ${props => props.align};
  ${({padding}) =>
    css`
      ${p(padding)}
    `}
`;
CardContentWrapper.displayName = 'CardContentWrapper';

const ContentContainer = styled.div`
  width: 100%;
`;

export const HorizontalLayoutColumns = styled(({collapseBelow, reverse, ...rest}) => <Columns collapseBelow={collapseBelow} reverse={reverse} {...rest} />)`
  width: 100%;

  ${({collapseBelow, reverse}) =>
    collapseBelow &&
    css`
      // When we are below the collapseBelow breakpoint:
      ${breakpoint('xs', collapseBelow)`
            display: flex;
            flex-direction: ${reverse ? `column` : `column-reverse`};

            > * {
              width: 100%;
              flex: none; // Unset flex shorthand on Column
            }
            `}
    `}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CardTag = styled(({reverse, ...rest}) => <div {...rest} />)`
  position: absolute;
  top: 0.5rem;
  ${({reverse}) => (reverse ? 'left: 0.5rem' : 'right: 0.5rem')};

  z-index: 5;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars, jsx-a11y/alt-text
const CardImage = styled(({horizontalLayout, ...rest}) => <img {...rest} />)`
  max-width: 100%;
  height: auto;

  ${({horizontalLayout}) =>
    horizontalLayout &&
    `
      object-fit: cover;
      height: 100%;
      width: 100%;
    `}
`;

const CardDivider = styled(Divider)`
  width: 100%;
  border-color: hsla(0, 0%, 0%, 0.13);
`;

const CardContent: React.FC<CardContentProps> = ({title, titleSize, titleComponent, padding, align, children}) => (
  <CardContentWrapper padding={padding} align={align} grow>
    <ContentContainer>
      <Stack space={4}>
        <Heading size={titleSize} component={titleComponent}>
          {title}
        </Heading>
        {children}
      </Stack>
    </ContentContainer>
  </CardContentWrapper>
);

CardContent.displayName = 'Card.Content';

const baseCardContentPropTypes = {
  title: PropTypes.string.isRequired,
  titleSize: PropTypes.oneOf(validTitleSizeValues),
  titleComponent: PropTypes.oneOf(validTitleComponentValues),
  padding: spacingPropTypeMap,
  align: PropTypes.oneOf(validAlignValues)
};

CardContent.propTypes = {
  ...baseCardContentPropTypes,
  children: PropTypes.node
};

CardContent.defaultProps = {
  titleSize: 3,
  titleComponent: 'h3'
};

const CardFooter: React.FC<CardFooterProps> = ({padding, align, children}) => (
  <>
    <CardDivider />
    <CardContentWrapper padding={padding} align={align}>
      <ContentContainer>
        <Stack space={4}>{children}</Stack>
      </ContentContainer>
    </CardContentWrapper>
  </>
);

CardFooter.displayName = 'Card.Footer';

CardFooter.propTypes = {
  padding: spacingPropTypeMap,
  align: PropTypes.oneOf(validAlignValues),
  children: PropTypes.node
};

export const getBackgroundColor = (background?: backgroundValues) => {
  let backgroundColor = '';
  switch (background) {
    case 'light':
      backgroundColor = 'lightest';
      break;
    case 'sageGreen':
      backgroundColor = 'sageGreen80';
      break;
    case 'warmWhite':
      backgroundColor = 'warmWhite';
      break;
    case 'sunsetPink':
      backgroundColor = 'sunsetPink';
      break;
    default:
      backgroundColor = 'white';
      break;
  }
  return backgroundColor;
};

const renderCardImage = (image: imageTypes, imageAlt?: string, imageLazyLoading?: boolean, horizontalLayout?: boolean) => {
  if (typeof image === 'string') {
    return <CardImage src={image} alt={imageAlt} loading={imageLazyLoading ? 'lazy' : 'eager'} horizontalLayout={horizontalLayout} />;
  } else {
    return image;
  }
};

const Card: React.FC<CardProps> & {
  Content: React.FC<CardContentProps>;
  Footer: React.FC<CardFooterProps>;
} = props => {
  const {children, component, horizontalLayout, collapseBelow, image, imageAlt, imageLazyLoading, icon: Icon, tag: Tag, align, padding, height, background, reverse, ...otherProps} = props;

  return horizontalLayout ? (
    <CardWrapper component={component} height={height} collapseBelow={collapseBelow} horizontalLayout {...otherProps}>
      <HorizontalLayoutColumns collapseBelow={collapseBelow} reverse={horizontalLayout && reverse}>
        <Column width="1/2">
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                padding,
                align
              });
            }
            return null;
          })}
        </Column>
        <Column width="1/2" flex>
          <CardHeader align={align} background={getBackgroundColor(background)}>
            {Tag && (
              <CardTag reverse={horizontalLayout && reverse}>
                <Tag />
              </CardTag>
            )}
            {image && !Icon && renderCardImage(image, imageAlt, imageLazyLoading, horizontalLayout)}
            {!image && Icon && (
              <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%" padding={8} background={getBackgroundColor(background)}>
                <Icon size={72} />
              </Box>
            )}
          </CardHeader>
        </Column>
      </HorizontalLayoutColumns>
    </CardWrapper>
  ) : (
    <CardWrapper component={component} height={height} horizontalLayout={false} {...otherProps}>
      <CardHeader align={align} background={getBackgroundColor(background)}>
        {Tag && (
          <CardTag>
            <Tag />
          </CardTag>
        )}
        {image && renderCardImage(image, imageAlt, imageLazyLoading)}
        {!image && Icon && (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%" padding={8} background={getBackgroundColor(background)}>
            <Icon size={72} />
          </Box>
        )}
      </CardHeader>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            padding,
            align
          });
        }
        return null;
      })}
    </CardWrapper>
  );
};

const basePropTypes = {
  // 'any' is required due to our use of GatsbyJS's Link component which doesn't fall under any defined PropType.
  component: PropTypes.any,
  align: PropTypes.oneOf(validAlignValues),
  padding: spacingPropTypeMap,
  height: PropTypes.string,
  background: PropTypes.oneOf(validBackgroundValues)
};

const baseDefaultProps = {
  component: 'div',
  align: 'left' as const,
  padding: 4 as const,
  background: 'white' as const
};

Card.displayName = 'Card';
Card.Content = CardContent;
Card.Footer = CardFooter;

Card.propTypes = {
  ...basePropTypes,
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  imageAlt: PropTypes.string,
  imageLazyLoading: PropTypes.bool,
  icon: PropTypes.func,
  tag: PropTypes.func,
  horizontalLayout: PropTypes.bool,
  reverse: PropTypes.bool,
  collapseBelow: PropTypes.oneOf(validBreakpointValues),
  children: PropTypes.node.isRequired
};

Card.defaultProps = {
  ...baseDefaultProps,
  imageLazyLoading: true,
  collapseBelow: 'md'
};

const FlashCard: React.FC<FlashCardProps> & {
  Content: React.FC<CardContentProps>;
  Footer: React.FC<CardFooterProps>;
} = props => {
  const {children, component, icon: Icon, align, padding, height, background, ...otherProps} = props;

  return (
    <CardWrapper component={component} height={height} {...otherProps}>
      <Columns>
        <Column width="content" flex>
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" padding={6} background={getBackgroundColor(background)}>
            {Icon && <Icon size="lg" />}
          </Box>
        </Column>
        <Column>
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                padding,
                align
              });
            }
            return null;
          })}
        </Column>
      </Columns>
    </CardWrapper>
  );
};

FlashCard.displayName = 'FlashCard';
FlashCard.Content = CardContent;
FlashCard.Footer = CardFooter;

FlashCard.propTypes = {
  ...basePropTypes,
  icon: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

FlashCard.defaultProps = {
  ...baseDefaultProps,
  background: 'light'
};

export {FlashCard};
export default Card;
